<template>
    <div class="tw:bg-white tw:rounded-xl">
        <BaseTable
            :columns="columns"
            :data="categorias"
            :skeleton="loading"
            :skeleton-rows="5"
            empty-text="No hay categorías disponibles"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers -->
            <template #skeleton-header-id>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-8"></div>
            </template>
            <template #skeleton-header-name>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>
            <template #skeleton-header-precio>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-indumentarias>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-header-observacion_indumentarias>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-28"></div>
            </template>
            <template #skeleton-header-activar_pago_plazos>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-header-taquilla>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-header-online>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-12"></div>
            </template>
            <template #skeleton-header-representante>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-full tw:mx-auto"></div>
            </template>

            <!-- Skeleton rows -->
            <template #skeleton-id>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-8"></div>
            </template>
            <template #skeleton-name>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-precio>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>
            <template #skeleton-indumentarias>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-28"></div>
            </template>
            <template #skeleton-observacion_indumentarias>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-48"></div>
            </template>
            <template #skeleton-activar_pago_plazos>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded-full"></div>
            </template>
            <template #skeleton-taquilla>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded-full"></div>
            </template>
            <template #skeleton-online>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded-full"></div>
            </template>
            <template #skeleton-representante>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-36"></div>
            </template>
            <template #skeleton-opciones>
                <div class="tw:h-9 tw:w-28 tw:bg-gray-200 tw:rounded tw:mx-auto"></div>
            </template>

            <template #cell-id="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.id }}</span>
            </template>

            <template #cell-name="{ row }">
                <span class="tw:text-sm tw:font-medium tw:text-gray-900">{{ row.name }}</span>
            </template>

            <template #cell-precio="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.precio != null ? Number(row.precio).toFixed(2) + ' €' : '' }}</span>
            </template>

            <template #cell-indumentarias="{ row }">
                <span v-if="row.indumentariasSelect?.length" class="tw:text-sm tw:text-gray-700">
                    {{ row.indumentariasSelect.map(i => i.nombre).join(', ') }}
                </span>
                <span v-else class="tw:text-sm tw:text-gray-400">No tiene indumentarias</span>
            </template>

            <template #cell-observacion_indumentarias="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.observacion_indumentarias || '' }}</span>
            </template>

            <template #cell-activar_pago_plazos="{ row }">
                <i v-if="row.activar_pago_plazos" class="text-green fas fa-check-circle tw:text-lg"></i>
                <i v-else class="text-danger fas fa-times-circle tw:text-lg"></i>
            </template>

            <template #cell-taquilla="{ row }">
                <i v-if="row.taquilla" class="text-green fas fa-check-circle tw:text-lg"></i>
                <i v-else class="text-danger fas fa-times-circle tw:text-lg"></i>
            </template>

            <template #cell-online="{ row }">
                <i v-if="row.online" class="text-green fas fa-check-circle tw:text-lg"></i>
                <i v-else class="text-danger fas fa-times-circle tw:text-lg"></i>
            </template>

            <template #cell-representante="{ row }">
                <span class="tw:text-sm tw:text-gray-700">{{ row.representante?.name || '' }}</span>
            </template>

            <template #cell-opciones="{ row, index }">
                <OpcionesDropdownTableComponent
                    :last-index="categorias.length > 3 && index >= categorias.length - 3"
                    :options="getRowOptions(row)"
                />
            </template>
        </BaseTable>

        <CategoriaEditModal
            v-if="editItem"
            name="edit-categoria"
            :model-value="editItem"
            :loading="editSaving"
            @save="(data) => emit('edit-save', data)"
            @close="emit('close-edit')"
        />
        <BorrarModalComponent
            v-if="deleteItem"
            name="borrar-categoria"
            :title="'Borrar categoría ' + (deleteItem?.name || '')"
            @confirm="emit('delete', deleteItem.id)"
            @close="emit('close-delete')"
        />
    </div>
</template>

<script setup>
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import OpcionesDropdownTableComponent from '@/modules/admin/ui/OpcionesDropdownTableComponent.vue'
    import CategoriaEditModal from './CategoriaEditModal.vue'
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue'

    const props = defineProps({
        categorias: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        listaId: {
            type: [String, Number],
            default: null
        },
        invitacionEnabled: {
            type: Boolean,
            default: false
        },
        editItem: {
            type: Object,
            default: null
        },
        defaultColWidth: {
            type: String,
            default: '120px'
        },
        deleteItem: {
            type: Object,
            default: null
        },
        editSaving: {
            type: Boolean,
            default: false
        }
    })

    const emit = defineEmits(['delete', 'refresh', 'edit-save', 'open-edit', 'open-delete', 'close-edit', 'close-delete'])

    const columns = [
        { key: 'id', label: 'ID', class: 'tw:w-16' },
        { key: 'name', label: 'Nombre', class: 'tw:w-32 ' },
        { key: 'precio', label: 'Precio', class: 'tw:w-28' },
        { key: 'indumentarias', label: 'Indumentarias', class: 'tw:w-44 ' },
        { key: 'observacion_indumentarias', label: 'Observación Indumentarias', class: 'tw:w-44 ' },
        { key: 'activar_pago_plazos', label: 'Pago a plazos', class: 'tw:w-28', centered:true },
        { key: 'taquilla', label: 'Taquilla', class: 'tw:w-24', centered:true },
        { key: 'online', label: 'Online', class: 'tw:w-24', centered:true },
        { key: 'representante', label: 'Representante', class: 'tw:w-40 ' },
        { key: 'opciones', label: 'Acciones', class: 'tw:w-32', centered: true }
    ]

    function getRowOptions(row) {
        const options = [
            { type: 'action', action: () => emit('open-edit', row), label: 'Editar', icon: 'edit' },
            { type: 'action', action: () => emit('open-delete', row), label: 'Borrar', icon: 'trash' }
        ]

        const lid = props.listaId || row.lista_id

        if (props.invitacionEnabled) {
            options.push({
                to: {
                    name: 'back.bases.categorias.codigos_invitacion',
                    params: { lista_id: lid, categoria_id: row.id }
                },
                label: 'Códigos invitación',
                icon: 'fas fa-ticket-alt'
            })
        }

        options.push({
            to: {
                name: 'back.bases.categorias.resumen',
                params: { lista_id: lid, categoria_id: row.id }
            },
            label: 'Resumen',
            icon: 'file-alt'
        })

        return options
    }
</script>
